import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import apiClient from '../../services/apiClient';
import CloseTradeDialog, { type CloseTradeData } from '../shared/CloseTradeDialog';

const MyTrades: React.FC = () => {
  const navigate = useNavigate();
  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [closeTarget, setCloseTarget] = useState<any | null>(null);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    try {
      // Always load all trades for summary cards; filter in the UI
      const result = await userService.listTrades({});
      setTrades(result);
    } catch {
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  // Kick off background price refresh on mount (non-blocking)
  useEffect(() => {
    apiClient.post('/api/users/trades/refresh-prices').catch(() => {});
  }, []);

  // Refresh live prices every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchTrades, 60000);
    return () => clearInterval(interval);
  }, [fetchTrades]);

  const handleClose = async (data: CloseTradeData) => {
    if (!closeTarget) return;
    await userService.closeTrade(closeTarget.id, {
      close_reason: data.close_reason,
      close_date: data.close_date,
      close_price: data.close_price,
      contracts: data.contracts,
    });
    fetchTrades();
  };

  const handleDelete = async (id: number) => {
    await userService.deleteTrade(id);
    fetchTrades();
  };

  const filtered = trades.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (sourceFilter !== 'all' && t.source !== sourceFilter) return false;
    return true;
  });

  const computePnl = (t: any) => {
    if (t.status === 'closed') return t.realized_pnl != null ? parseFloat(t.realized_pnl) : null;
    if (t.live_price == null || t.entry_price == null) return null;
    const entry = Math.round(parseFloat(t.entry_price) * 100) / 100;
    const live = Math.round(t.live_price * 100) / 100;
    const isShares = t.strategy_name === 'assigned_shares';
    const isCredit = t.trade_direction === 'credit';
    const multiplier = isShares ? t.contracts : t.contracts * 100;
    const diff = isShares ? (live - entry) : (isCredit ? (entry - live) : (live - entry));
    return Math.round(diff * multiplier * 100) / 100;
  };

  const strategyLabel = (s: string) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

  const legsSummary = (legs: any[]) => {
    if (!legs || legs.length === 0) return '-';
    return legs.map((l: any) => {
      const prefix = l.side === 'buy' ? '+' : '-';
      const type = l.option_type === 'C' ? 'C' : 'P';
      return `${prefix}$${l.strike}${type}`;
    }).join(' / ');
  };

  // ── TOTAL stats (all trades, ignoring filters) ──
  const totalOpen = trades.filter((t) => t.status === 'open').length;
  const totalClosed = trades.filter((t) => t.status === 'closed').length;
  const totalRealized = trades.filter((t) => t.realized_pnl != null).reduce((sum, t) => sum + parseFloat(t.realized_pnl), 0);
  const totalUnrealized = trades.filter((t) => t.status === 'open' && t.live_price != null && t.entry_price != null)
    .reduce((sum, t) => sum + (computePnl(t) ?? 0), 0);
  const totalPnl = totalRealized + totalUnrealized;
  const wins = trades.filter((t) => t.status === 'closed' && t.realized_pnl != null && parseFloat(t.realized_pnl) > 0);
  const losses = trades.filter((t) => t.status === 'closed' && t.realized_pnl != null && parseFloat(t.realized_pnl) < 0);
  const winRate = totalClosed > 0 ? Math.round((wins.length / totalClosed) * 100) : null;
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + parseFloat(t.realized_pnl), 0) / wins.length : null;
  const avgLoss = losses.length > 0 ? losses.reduce((s, t) => s + parseFloat(t.realized_pnl), 0) / losses.length : null;

  // ── FILTERED stats (based on current filters) ──
  const filtRealized = filtered.filter((t) => t.realized_pnl != null).reduce((sum, t) => sum + parseFloat(t.realized_pnl), 0);
  const filtUnrealized = filtered.filter((t) => t.status === 'open' && t.live_price != null && t.entry_price != null)
    .reduce((sum, t) => sum + (computePnl(t) ?? 0), 0);

  const pnlColor = (v: number) => v >= 0 ? 'success.main' : 'error.main';
  const pnlText = (v: number) => `${v >= 0 ? '+' : ''}$${v.toFixed(0)}`;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>My Trades</Typography>

      {/* ── TOTAL PORTFOLIO bar ── */}
      <Paper sx={{ px: 3, py: 1.5, mb: 2, bgcolor: 'action.selected', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, mr: 1 }}>TOTAL PORTFOLIO</Typography>
        <Box>
          <Typography variant="caption" color="text.secondary">Total P&L</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: pnlColor(totalPnl), lineHeight: 1.2 }}>
            {pnlText(totalPnl)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Win Rate</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {winRate != null ? `${winRate}%` : '-'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Avg Win</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'success.main' }}>
            {avgWin != null ? `+$${avgWin.toFixed(0)}` : '-'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Avg Loss</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'error.main' }}>
            {avgLoss != null ? `$${avgLoss.toFixed(0)}` : '-'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Open</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{totalOpen}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Closed</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{totalClosed}</Typography>
        </Box>
      </Paper>

      {/* ── Filter bar + filtered stats ── */}
      <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Source</InputLabel>
          <Select value={sourceFilter} label="Source" onChange={(e) => setSourceFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pulse">Pulse</MenuItem>
            <MenuItem value="earnings">Earnings</MenuItem>
            <MenuItem value="wheel">Wheel</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">{filtered.length} trade{filtered.length !== 1 ? 's' : ''}</Typography>
        <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 2, display: 'flex', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Realized</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: pnlColor(filtRealized) }}>{pnlText(filtRealized)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Unrealized</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: pnlColor(filtUnrealized) }}>{pnlText(filtUnrealized)}</Typography>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">No trades found. Enter trades from the Pulse page.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Legs</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Contracts</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Entry</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Live Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">P&L</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Opened</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((t) => {
                const pnl = computePnl(t);
                return (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (t.source === 'wheel' && t.wheel_strategy_id) navigate(`/portfolio/wheel/${t.wheel_strategy_id}`);
                      else if (t.source === 'earnings' && t.earnings_date) navigate(`/discover/earnings/${t.ticker}?date=${t.earnings_date}`);
                      else navigate(`/pulse/${t.ticker}`);
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.ticker}
                      </Typography>
                    </TableCell>
                    <TableCell>{strategyLabel(t.strategy_name)}</TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{legsSummary(t.legs)}</Typography></TableCell>
                    <TableCell align="right">{t.contracts}</TableCell>
                    <TableCell align="right">${(parseFloat(t.entry_price) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{t.live_price != null ? `$${t.live_price.toFixed(2)}` : '-'}</TableCell>
                    <TableCell align="right">
                      {pnl != null ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: pnl >= 0 ? 'success.main' : 'error.main' }}>
                          {pnl >= 0 ? '+' : ''}${pnl.toFixed(0)}
                        </Typography>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={t.status} size="small" color={t.status === 'open' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>{t.entry_date}</TableCell>
                    <TableCell>{t.legs?.[0]?.expiry || '-'}</TableCell>
                    <TableCell><Chip label={t.source} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      {t.status === 'open' && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" variant="outlined" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }} onClick={(e) => { e.stopPropagation(); setCloseTarget(t); }}>
                            Close
                          </Button>
                          <Button size="small" color="error" sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.7rem' }} onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }}>
                            Delete
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {closeTarget && (
        <CloseTradeDialog
          open={!!closeTarget}
          ticker={closeTarget.ticker}
          strategyName={strategyLabel(closeTarget.strategy_name)}
          legs={closeTarget.legs || []}
          totalContracts={closeTarget.contracts}
          entryPrice={parseFloat(closeTarget.entry_price)}
          tradeDirection={closeTarget.trade_direction}
          onClose={() => setCloseTarget(null)}
          onSubmit={handleClose}
        />
      )}
    </Box>
  );
};

export default MyTrades;
